import { createFileRoute, Link } from "@tanstack/react-router";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Heart,
  Shuffle,
  ArrowLeft,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  AFFIRMATION_CARD_CATEGORIES,
  type AffirmationCardCategoryId,
  buildDailyDeck,
  getCardsForCategories,
  hashCardText,
} from "@/lib/affirmation-cards-data";
import { useFavorite } from "@/hooks/useFavorite";
import selfCompassionImg from "@/assets/affirmations/self-compassion.jpg";
import selfWorthImg from "@/assets/affirmations/self-worth.jpg";
import boundariesImg from "@/assets/affirmations/boundaries.jpg";
import recoveryImg from "@/assets/affirmations/recovery.jpg";
import anxietyImg from "@/assets/affirmations/anxiety.jpg";
import griefImg from "@/assets/affirmations/grief.jpg";
import relationshipsImg from "@/assets/affirmations/relationships.jpg";
import hopeImg from "@/assets/affirmations/hope.jpg";

export const Route = createFileRoute("/affirmations")({
  head: () => ({
    meta: [
      { title: "Affirmation Cards — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A trauma-informed affirmation library of 250+ gentle cards across 20 themes. A different card each day, filter by what you need, save your favourites.",
      },
      { property: "og:title", content: "Affirmation Cards — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "A gentle library of 250+ trauma-informed affirmations — self-worth, safety, anxiety, PTSD, CPTSD, inner child, boundaries, grief, hope and more.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/affirmations" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/affirmations" }],
  }),
  component: AffirmationCardsPage,
});

const FAV_KEY = "rt:fav-affirmations";
const STREAK_KEY = "rt:affirmations-streak";
const RECENT_KEY = "rt:affirmations-recent";
const RECENT_MAX = 40;

// Map each category to a watercolour background. New categories reuse the
// closest-in-tone existing artwork so the UI stays consistent without
// requiring new assets for every theme.
const CATEGORY_ART: Record<AffirmationCardCategoryId, string> = {
  self_worth: selfWorthImg,
  self_compassion: selfCompassionImg,
  safety: recoveryImg,
  anxiety: anxietyImg,
  ptsd: recoveryImg,
  cptsd: recoveryImg,
  inner_child: hopeImg,
  relationships: relationshipsImg,
  boundaries: boundariesImg,
  grief: griefImg,
  hope: hopeImg,
  strength: recoveryImg,
  nervous_system: anxietyImg,
  sleep: griefImg,
  trust: selfCompassionImg,
  forgiveness: selfCompassionImg,
  daily_motivation: hopeImg,
  emotional_regulation: anxietyImg,
  self_acceptance: selfWorthImg,
  growth: recoveryImg,
  recovery: recoveryImg,
};


type Card = { text: string; category: AffirmationCardCategoryId };
type Deck = Card[];

function makeCardId(c: Card) {
  let h = 0;
  for (let i = 0; i < c.text.length; i++) h = (h * 31 + c.text.charCodeAt(i)) >>> 0;
  return `${c.category}-${h.toString(36)}`;
}

/* ---------- Streak tracking (client only) ---------- */

type StreakState = { day: number; viewedToday: number };

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function yesterdayISO(fromISO: string) {
  const [y, m, d] = fromISO.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function useAffirmationStreak() {
  const [state, setState] = useState<StreakState | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STREAK_KEY);
      const today = todayISO();
      const parsed = raw ? (JSON.parse(raw) as { lastDate?: string; day?: number; viewedToday?: number }) : null;
      let next: { lastDate: string; day: number; viewedToday: number };
      if (!parsed || !parsed.lastDate) {
        next = { lastDate: today, day: 1, viewedToday: 1 };
      } else if (parsed.lastDate === today) {
        next = { lastDate: today, day: parsed.day ?? 1, viewedToday: (parsed.viewedToday ?? 0) + 1 };
      } else if (parsed.lastDate === yesterdayISO(today)) {
        next = { lastDate: today, day: (parsed.day ?? 0) + 1, viewedToday: 1 };
      } else {
        next = { lastDate: today, day: 1, viewedToday: 1 };
      }
      localStorage.setItem(STREAK_KEY, JSON.stringify(next));
      setState({ day: next.day, viewedToday: next.viewedToday });
    } catch {
      setState({ day: 1, viewedToday: 1 });
    }
  }, []);

  const bump = useCallback(() => {
    try {
      const raw = localStorage.getItem(STREAK_KEY);
      const today = todayISO();
      const parsed = raw ? (JSON.parse(raw) as { lastDate?: string; day?: number; viewedToday?: number }) : null;
      const day = parsed?.day ?? 1;
      const viewedToday = parsed && parsed.lastDate === today ? (parsed.viewedToday ?? 0) + 1 : 1;
      localStorage.setItem(STREAK_KEY, JSON.stringify({ lastDate: today, day, viewedToday }));
      setState({ day, viewedToday });
    } catch {
      /* ignore */
    }
  }, []);

  return { state, bump };
}

/* ---------- Page ---------- */

type FilterId = "all" | AffirmationCardCategoryId;

function AffirmationCardsPage() {
  const [filter, setFilter] = useState<FilterId>("all");
  const activeIds = useMemo<AffirmationCardCategoryId[]>(
    () => (filter === "all" ? AFFIRMATION_CARD_CATEGORIES.map((c) => c.id) : [filter]),
    [filter],
  );

  // Recently-viewed IDs (client only). Used to de-prioritise repeats.
  const [recentIds, setRecentIds] = useState<string[]>([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecentIds(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
  }, []);

  const pushRecent = useCallback((text: string) => {
    setRecentIds((prev) => {
      const id = hashCardText(text);
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, RECENT_MAX);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const [deck, setDeck] = useState<Deck>(() =>
    buildDailyDeck(getCardsForCategories(AFFIRMATION_CARD_CATEGORIES.map((c) => c.id)), {
      dateISO: todayISO(),
      recentIds: [],
    }),
  );
  const [index, setIndex] = useState(0);

  // Rebuild deck when filter changes (uses latest recent list).
  useEffect(() => {
    setDeck(
      buildDailyDeck(getCardsForCategories(activeIds), {
        dateISO: todayISO(),
        recentIds,
      }),
    );
    setIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIds]);

  const card = deck[index];
  const nextCard = deck[(index + 1) % Math.max(deck.length, 1)];

  const { state: streak, bump } = useAffirmationStreak();

  // Track "recently viewed" as cards are shown.
  useEffect(() => {
    if (card?.text) pushRecent(card.text);
  }, [card?.text, pushRecent]);

  const advance = useCallback(
    (direction: 1 | -1 = 1) => {
      setIndex((i) => {
        if (!deck.length) return 0;
        return (i + direction + deck.length) % deck.length;
      });
      bump();
    },
    [deck.length, bump],
  );

  const reshuffle = () => {
    setDeck(
      buildDailyDeck(getCardsForCategories(activeIds), {
        dateISO: todayISO() + "|" + Math.random().toString(36).slice(2, 8),
        recentIds,
      }),
    );
    setIndex(0);
  };


  const tabs: { id: FilterId; label: string }[] = [
    { id: "all", label: "All" },
    ...AFFIRMATION_CARD_CATEGORIES.map((c) => ({ id: c.id as FilterId, label: c.label })),
  ];

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto flex max-w-2xl flex-col px-5 pb-24 pt-6 sm:px-8">
        {/* Top row */}
        <div className="mb-8 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Daily Support
          </Link>
          <Link
            to="/favorites"
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold-light/20 px-4 py-2 text-xs font-medium text-[var(--gold-deep)] hover:bg-gold-light/30"
          >
            <Heart className="h-3.5 w-3.5" />
            My Favourites
          </Link>
        </div>

        {/* Streak header */}
        <header className="mb-8 text-center">
          <p className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[var(--gold-deep)]">
            <Sparkles className="h-3 w-3" />
            Daily Support
          </p>
          <h1
            className="mx-auto mt-3 max-w-lg text-[38px] leading-[1.1] sm:text-[46px]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, letterSpacing: "-0.01em" }}
          >
            Affirmation Cards
          </h1>
          {streak ? (
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-foreground/65">
              <span className="font-medium text-foreground/85">
                Day {streak.day} of your recovery journey.
              </span>{" "}
              You've read {streak.viewedToday} affirmation{streak.viewedToday === 1 ? "" : "s"} today.
            </p>
          ) : (
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-foreground/65">
              A gentle deck to open, slowly, whenever you need it.
            </p>
          )}
        </header>

        {/* Filter tabs */}
        <div className="mb-8">
          <div
            className="flex flex-wrap justify-center gap-2"
            role="tablist"
            aria-label="Affirmation categories"
          >
            {tabs.map((t) => {
              const on = filter === t.id;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={on}
                  type="button"
                  onClick={() => setFilter(t.id)}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-medium transition-all ${
                    on
                      ? "border-[var(--gold-deep)] bg-transparent text-[var(--gold-deep)]"
                      : "border-gold/30 bg-transparent text-foreground/70 hover:border-gold/60 hover:text-foreground"
                  }`}
                
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>


        {/* Deck */}
        <Deck card={card} nextCard={nextCard} onSwiped={advance} />

        {/* Arrow pager — matches the members-preview carousel */}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => advance(-1)}
            aria-label="Previous affirmation"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 bg-card text-foreground shadow-sm transition hover:bg-cream"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/55">
            {Math.min(index + 1, deck.length)}{" "}
            <span className="mx-1 text-foreground/30">/</span> {deck.length}
          </p>
          <button
            type="button"
            onClick={() => advance(1)}
            aria-label="Next affirmation"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 bg-card text-foreground shadow-sm transition hover:bg-cream"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] uppercase tracking-[0.18em] text-foreground/45">
          Drag or tap the arrows
        </p>

        {/* Shuffle — subtle secondary action */}
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={reshuffle}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-card px-4 py-2 text-xs font-medium text-foreground/70 transition hover:bg-cream hover:text-foreground"
          >
            <Shuffle className="h-3.5 w-3.5" strokeWidth={1.75} />
            Shuffle the deck
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Deck ---------- */

function Deck({
  card,
  nextCard,
  onSwiped,
}: {
  card: Card | undefined;
  nextCard: Card | undefined;
  onSwiped: (direction: 1 | -1) => void;
}) {
  if (!card) {
    return (
      <div className="mx-auto max-w-sm rounded-[32px] border border-gold/30 bg-gradient-to-br from-card via-gold-light/40 to-card p-10 text-center text-sm text-foreground/60">
        No cards in this selection.
      </div>
    );
  }

  return (
    <div className="relative mx-auto h-[440px] w-full max-w-sm sm:h-[480px]">
      {/* Stacked backdrops for the deck feel */}
      <div
        aria-hidden
        className="absolute inset-0 translate-y-3 scale-[0.96] rounded-[32px] border border-gold/30 bg-gradient-to-br from-card via-gold-light/25 to-card shadow-sm"
      />
      {nextCard && nextCard.text !== card.text && (
        <div
          aria-hidden
          className="absolute inset-0 translate-y-1.5 scale-[0.98] rounded-[32px] border border-gold/35 bg-gradient-to-br from-card via-gold-light/30 to-card shadow-sm"
        />
      )}
      <SwipeableCard key={`front-${card.text}`} card={card} onSwiped={onSwiped} />
    </div>
  );
}

function SwipeableCard({
  card,
  onSwiped,
}: {
  card: Card;
  onSwiped: (direction: 1 | -1) => void;
}) {
  const cat = AFFIRMATION_CARD_CATEGORIES.find((c) => c.id === card.category)!;
  const art = CATEGORY_ART[card.category];
  const cardId = useMemo(() => `${card.category}-${hashCardText(card.text)}`, [card]);
  const { saved, toggle } = useFavorite(FAV_KEY, cardId);

  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const [leaving, setLeaving] = useState<null | "left" | "right">(null);
  const [entered, setEntered] = useState(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDrag({ x: 0, y: 0, dragging: false });
    setLeaving(null);
    setEntered(false);
    const t = window.setTimeout(() => setEntered(true), 20);
    return () => window.clearTimeout(t);
  }, [card]);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button, a")) return;
    elRef.current?.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    setDrag((d) => ({ ...d, dragging: true }));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!startRef.current) return;
    setDrag({
      x: e.clientX - startRef.current.x,
      y: (e.clientY - startRef.current.y) * 0.25,
      dragging: true,
    });
  };
  const endDrag = () => {
    if (!startRef.current) return;
    const { x } = drag;
    startRef.current = null;
    const threshold = 100;
    if (Math.abs(x) > threshold) {
      const dir: "left" | "right" = x > 0 ? "right" : "left";
      setLeaving(dir);
      window.setTimeout(() => onSwiped(dir === "left" ? -1 : 1), 340);
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rotate = drag.x * 0.04;
  const flyX = leaving === "right" ? 700 : leaving === "left" ? -700 : drag.x;
  const flyRot = leaving ? (leaving === "right" ? 18 : -18) : rotate;
  const enterTransform = !entered
    ? "translateY(14px) scale(0.98)"
    : `translate(${flyX}px, ${drag.y}px) rotate(${flyRot}deg)`;
  const transition = drag.dragging
    ? "none"
    : "transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 420ms ease";
  const opacity = leaving ? 0 : entered ? 1 : 0;

  return (
    <div
      ref={elRef}
      role="group"
      aria-label={`${cat.label} affirmation. Drag left or right for the next card.`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="absolute inset-0 z-10 flex touch-none select-none flex-col items-center justify-between overflow-hidden rounded-[32px] border border-gold/45 bg-gradient-to-br from-card via-gold-light/40 to-card p-8 text-center sm:p-10"
      style={{
        boxShadow:
          "0 30px 70px -20px rgba(184,134,11,0.25), 0 8px 20px -8px rgba(61,43,31,0.10)",
        transform: enterTransform,
        transition,
        opacity,
        cursor: drag.dragging ? "grabbing" : "grab",
      }}
    >
      {/* Category pill (mirrors the "MEMBERS" pill in the preview) */}
      <div className="relative z-10 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)] backdrop-blur-sm">
        <span aria-hidden className="text-xs leading-none">
          {cat.emoji}
        </span>
        {cat.label}
      </div>

      {/* Icon tile + affirmation text */}
      <div className="relative z-10 flex flex-col items-center gap-4">
        <div
          aria-hidden
          className="relative flex h-24 w-24 items-center justify-center rounded-3xl bg-[radial-gradient(ellipse_at_center,_var(--cream)_0%,_color-mix(in_oklab,var(--gold-light)_55%,var(--cream))_75%,_transparent_100%)] ring-1 ring-gold-light/50 sm:h-28 sm:w-28"
        >
          <img
            src={art}
            alt=""
            loading="lazy"
            draggable={false}
            className="h-full w-full rounded-3xl object-cover p-1 opacity-90"
          />
        </div>
        <blockquote
          className="max-w-[18rem] px-1 text-[22px] leading-[1.25] text-foreground sm:text-[26px]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            letterSpacing: "-0.005em",
          }}
        >
          "{card.text}"
        </blockquote>
      </div>

      {/* Save CTA — mirrors the "See what's included" pill */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggle({ label: card.text, sub: cat.label });
        }}
        aria-pressed={saved}
        aria-label={saved ? "Remove affirmation from favourites" : "Save affirmation to favourites"}
        className={`relative z-10 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-semibold shadow-sm backdrop-blur transition ${
          saved
            ? "border-transparent text-[var(--gold-deep)]"
            : "border-gold/50 bg-background/80 text-[var(--gold-deep)] hover:bg-cream"
        }`}
        style={saved ? { background: "var(--gradient-gold)" } : undefined}
      >
        <Heart className={`h-3.5 w-3.5 ${saved ? "fill-current" : ""}`} strokeWidth={2} aria-hidden />
        {saved ? "Saved to favourites" : "Save this affirmation"}
      </button>
    </div>
  );
}

